import { Quote } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import Autoplay from "embla-carousel-autoplay";

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  company: string;
  image_url: string | null;
  display_order: number;
}

interface TestimonialSettings {
  title: string;
  subtitle: string;
  autoplay_enabled: boolean;
  autoplay_interval: number;
}

export const SocialProof = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [settings, setSettings] = useState<TestimonialSettings>({
    title: "Zadovoljni naročniki",
    subtitle: "Startupi in partnerji, ki soustvarjajo prihodnost računovodstva v Sloveniji",
    autoplay_enabled: true,
    autoplay_interval: 3000,
  });

  useEffect(() => {
    const fetchData = async () => {
      // Fetch testimonials
      const { data: testimonialsData, error: testimonialsError } = await supabase
        .from("cms_testimonials")
        .select("*")
        .eq("active", true)
        .order("display_order");

      if (!testimonialsError && testimonialsData) {
        setTestimonials(testimonialsData);
      }

      // Fetch settings (title, subtitle, autoplay)
      const { data: settingsData, error: settingsError } = await supabase
        .from("cms_testimonial_settings")
        .select("*")
        .single();

      if (!settingsError && settingsData) {
        setSettings({
          title: settingsData.title,
          subtitle: settingsData.subtitle,
          autoplay_enabled: settingsData.autoplay_enabled,
          autoplay_interval: settingsData.autoplay_interval,
        });
      }
    };

    fetchData();
  }, []);

  if (testimonials.length === 0) return null;

  // Duplicate testimonials to ensure enough items for smooth looping
  const displayTestimonials = testimonials.length <= 4
    ? [...testimonials, ...testimonials]
    : testimonials;

  return (
    <section className="py-24 md:py-32 bg-muted/20">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-4xl font-bold mb-6 text-foreground md:text-5xl">{settings.title}</h2>
          <p className="text-xl text-muted-foreground">{settings.subtitle}</p>
        </div>

        <div className="mx-auto max-w-6xl">
          <Carousel
            opts={{
              align: "start",
              loop: true,
              dragFree: false,
            }}
            plugins={
              settings.autoplay_enabled
                ? [
                    Autoplay({
                      delay: settings.autoplay_interval,
                      stopOnInteraction: false,
                      stopOnMouseEnter: true,
                    }),
                  ]
                : []
            }
            className="w-full"
          >
            <CarouselContent>
              {displayTestimonials.map((testimonial, index) => (
                <CarouselItem key={`${testimonial.id}-${index}`} className="md:basis-1/2 lg:basis-1/3 pl-4">
                  <div className="text-center p-6 h-full flex flex-col">
                    <Quote className="h-10 w-10 text-secondary mx-auto mb-6 opacity-60" />
                    
                    <p className="text-lg text-foreground mb-6 leading-relaxed flex-grow">
                      "{testimonial.quote}"
                    </p>
                    
                    <div className="flex flex-col items-center gap-4">
                      <Avatar className="h-16 w-16">
                        {testimonial.image_url && (
                          <AvatarImage src={testimonial.image_url} alt={testimonial.author} />
                        )}
                        <AvatarFallback className="bg-secondary/20 text-foreground">
                          {testimonial.author.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div>
                        <div className="font-bold text-foreground">
                          {testimonial.author}
                        </div>
                        <div className="text-base text-muted-foreground">
                          {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        </div>
      </div>
    </section>
  );
};
